"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AffirmMessageElement,
  Elements,
  PaymentMethodMessagingElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import type {
  StripeAffirmMessageElementOptions,
  StripeElementsOptions,
  StripePaymentMethodMessagingElementOptions,
} from "@stripe/stripe-js";
import { cn } from "@/lib/utils";
import { formatPaymentAmount } from "@/utils/format";

interface InstallmentMessageProps {
  amount: number;
  currency?: string | null;
  countryCode?: StripePaymentMethodMessagingElementOptions["countryCode"];
  logoColor?: StripePaymentMethodMessagingElementOptions["logoColor"];
  paymentMethodTypes?: StripePaymentMethodMessagingElementOptions["paymentMethodTypes"];
  paymentMethodOrder?: StripePaymentMethodMessagingElementOptions["paymentMethodOrder"];
  textAlign?: StripeAffirmMessageElementOptions["textAlign"];
  textColor?: "dark" | "light";
  chip?: boolean;
  onVisibleMessage?: () => void;
  className?: string;
}

type MessagingCurrency = StripePaymentMethodMessagingElementOptions["currency"];
type MessagingPaymentMethod = NonNullable<
  StripePaymentMethodMessagingElementOptions["paymentMethodTypes"]
>[number];

const supportedCurrencies: MessagingCurrency[] = [
  "AUD",
  "CAD",
  "CHF",
  "CZK",
  "DKK",
  "EUR",
  "GBP",
  "NOK",
  "NZD",
  "PLN",
  "SEK",
  "USD",
];

const stripePromise =
  process.env.NODE_ENV !== "test" &&
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
    ? loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
    : null;

function getStripeAppearance(
  textColor: "dark" | "light"
): StripeElementsOptions["appearance"] {
  const isDark = textColor === "light";
  const text = isDark ? "#ffffff" : "#000000";
  const background = isDark ? "#18181b" : "#ffffff";

  return {
    theme: isDark ? "night" : "stripe",
    variables: {
      colorBackground: background,
      colorText: text,
      colorTextSecondary: text,
      accessibleColorOnColorBackground: text,
      fontSizeBase: "11px",
      fontWeightMedium: "500",
      spacingUnit: "1px",
    },
    rules: {
      ".PaymentMethodMessaging": {
        marginTop: "0px",
      },
    },
  };
}

function MessagingShell({
  children,
  chip,
  onVisibleMessage,
  className,
}: {
  children: ReactNode;
  chip: boolean;
  onVisibleMessage?: () => void;
  className?: string;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const [hasVisibleMessage, setHasVisibleMessage] = useState(false);

  useEffect(() => {
    const node = shellRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    let observedIframe: HTMLIFrameElement | null = null;

    const updateVisibility = () => {
      const iframe = node.querySelector("iframe");
      if (iframe && iframe !== observedIframe) {
        if (observedIframe) resizeObserver.unobserve(observedIframe);
        observedIframe = iframe;
        resizeObserver.observe(iframe);
      }

      const height =
        observedIframe?.getBoundingClientRect().height ??
        node.getBoundingClientRect().height;
      const isVisible = height > 6;
      setHasVisibleMessage(isVisible);
      if (isVisible) {
        onVisibleMessage?.();
      }
    };

    const resizeObserver = new ResizeObserver(updateVisibility);
    const mutationObserver = new MutationObserver(updateVisibility);
    const rafId = window.requestAnimationFrame(updateVisibility);
    const timeoutId = window.setTimeout(updateVisibility, 1500);

    resizeObserver.observe(node);
    mutationObserver.observe(node, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [onVisibleMessage]);

  return (
    <div
      ref={shellRef}
      className={cn(
        "mt-1 inline-block max-w-full text-[11px] font-medium leading-4 text-muted-foreground transition-colors sm:text-xs",
        chip &&
          hasVisibleMessage &&
          "rounded-md bg-white/90 px-2 py-0.5 text-zinc-700 shadow-sm ring-1 ring-black/10 dark:bg-white/90 dark:text-zinc-700",
        className
      )}
    >
      {children}
    </div>
  );
}

function normalizeCurrency(currency?: string | null): MessagingCurrency | null {
  const normalized = (currency || "USD").toUpperCase() as MessagingCurrency;
  return supportedCurrencies.includes(normalized) ? normalized : null;
}

function getDefaultPaymentMethods(): MessagingPaymentMethod[] {
  return ["klarna", "affirm"];
}

function getTestMessage(amount: number, currency?: string | null) {
  const normalizedCurrency = (currency || "USD").toUpperCase();
  return normalizedCurrency === "USD"
    ? `or 4 payments of ${formatPaymentAmount(amount / 4, normalizedCurrency)} with Klarna or Affirm`
    : "or pay over time with Klarna at checkout";
}

export default function InstallmentMessage({
  amount,
  currency,
  countryCode = "US",
  logoColor = "black",
  paymentMethodTypes,
  paymentMethodOrder,
  textAlign = "left",
  textColor = "dark",
  chip = true,
  onVisibleMessage,
  className,
}: InstallmentMessageProps) {
  const numericAmount = Number(amount || 0);
  const stripeAppearance = useMemo(
    () => getStripeAppearance(textColor),
    [textColor]
  );

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return null;
  }

  if (process.env.NODE_ENV === "test") {
    return (
      <p
        className={cn(
          "mt-1 text-[11px] font-medium leading-4 text-muted-foreground sm:text-xs",
          className
        )}
      >
        {getTestMessage(numericAmount, currency)}
      </p>
    );
  }

  const normalizedCurrency = normalizeCurrency(currency);
  const amountInCents = Math.round(numericAmount * 100);
  const methods =
    paymentMethodTypes ||
    (normalizedCurrency ? getDefaultPaymentMethods() : []);
  const shouldUseAffirmElement =
    normalizedCurrency === "USD" &&
    paymentMethodTypes?.length === 1 &&
    paymentMethodTypes[0] === "affirm";
  const affirmLogoColor: StripeAffirmMessageElementOptions["logoColor"] =
    logoColor === "color" ? "primary" : logoColor;
  const affirmOptions: StripeAffirmMessageElementOptions | null =
    shouldUseAffirmElement && amountInCents > 0
      ? {
          amount: amountInCents,
          currency: "USD",
          fontColor: textColor === "light" ? "#ffffff" : "#3f3a46",
          fontSize: "11px",
          logoColor: affirmLogoColor,
          textAlign,
        }
      : null;

  if (!stripePromise) {
    return null;
  }

  if (affirmOptions) {
    return (
      <MessagingShell
        chip={chip}
        onVisibleMessage={onVisibleMessage}
        className={className}
      >
        <Elements
          key={`affirm-${textColor}-${logoColor}`}
          stripe={stripePromise}
          options={{ appearance: stripeAppearance }}
        >
          <AffirmMessageElement options={affirmOptions} />
        </Elements>
      </MessagingShell>
    );
  }

  const messageOptions: StripePaymentMethodMessagingElementOptions | null =
    normalizedCurrency && amountInCents > 0
      ? {
          amount: amountInCents,
          currency: normalizedCurrency,
          countryCode,
          logoColor,
          paymentMethodTypes: methods,
          paymentMethodOrder: paymentMethodOrder || methods,
        }
      : null;

  if (!messageOptions) {
    return null;
  }

  return (
    <MessagingShell
      chip={chip}
      onVisibleMessage={onVisibleMessage}
      className={className}
    >
      <Elements
        key={`messaging-${textColor}-${logoColor}`}
        stripe={stripePromise}
        options={{ appearance: stripeAppearance }}
      >
        <PaymentMethodMessagingElement options={messageOptions} />
      </Elements>
    </MessagingShell>
  );
}
